import { isCurrencyEqual } from "../../../utils";
import { MonetaryAmount } from "@interlay/monetary-js";
import { isStablePlainMultiPathElement, MultiPathElementType, } from "../trade/types";
import { isStablePool, isStandardPool } from "./types";
const filterNonEmptyPools = (pools) => pools.filter(({ isEmpty }) => !isEmpty);
/**
 * Get all trading pairs based on provided pools.
 *
 * @param nonEmptyPools All standard and stable pools.
 * @returns {Array<TradingPair>} All trading pairs.
 */
const getAllTradingPairs = (pools) => {
    const nonEmptyPools = filterNonEmptyPools(pools);
    const stablePools = nonEmptyPools.filter(isStablePool);
    const pairs = [];
    nonEmptyPools.forEach((pool) => {
        if (isStandardPool(pool)) {
            // Exclude pool in Bootstrap status
            if (pool.isTradingActive) {
                pairs.push(pool);
            }
        }
        else {
            const stablePairs = convertStablePoolToTradingPairs(pool, stablePools);
            pairs.push(...stablePairs);
        }
    });
    return pairs;
};
function generateOutputFunction(pathOf) {
    return (inputAmount) => getStableSwapOutputAmount(pathOf(inputAmount.currency), inputAmount);
}
function convertPoolToTradingPair(pool, token0, token1) {
    if (!(pool.involvesToken(token0) && pool.involvesToken(token1))) {
        throw new Error("converPoolToTradingPair: provided currencies are not part of pool.");
    }
    const pathOf = (currency) => ({
        type: MultiPathElementType.STABLE_PLAIN,
        input: currency,
        output: isCurrencyEqual(currency, token0) ? token1 : token0,
        pool: pool,
    });
    return {
        token0,
        token1,
        reserve0: pool.pooledCurrencies[pool.getTokenIndex(token0)],
        reserve1: pool.pooledCurrencies[pool.getTokenIndex(token1)],
        getOutputAmount: generateOutputFunction(pathOf),
        pathOf,
    };
}
function convertPoolAndBaseToTradingPair(basePool, metaPool, token0, token1) {
    if (!(basePool.involvesToken(token0) && metaPool.involvesToken(token1))) {
        throw new Error("converPoolAndBaseToTradingPair: incorrect tokens provided");
    }
    const pathOf = (currency) => ({
        type: MultiPathElementType.STABLE_META,
        input: currency,
        output: isCurrencyEqual(currency, token0) ? token1 : token0,
        pool: metaPool,
        basePool: basePool,
        fromBase: !!isCurrencyEqual(currency, token0),
    });
    return {
        token0,
        token1,
        reserve0: basePool.actuallyPooledCurrencies[basePool.getTokenIndex(token0)],
        reserve1: metaPool.actuallyPooledCurrencies[metaPool.getTokenIndex(token1)],
        getOutputAmount: generateOutputFunction(pathOf),
        pathOf,
    };
}
const convertStablePoolToTradingPairs = (pool, stablePools) => {
    const pairs = [];
    const relatedMetaPools = (stablePools.filter((otherPool) => otherPool.involvesToken(pool.lpToken)));
    for (let j = 0; j < pool.actuallyPooledCurrencies.length; j++) {
        for (let k = j + 1; k < pool.actuallyPooledCurrencies.length; k++) {
            const token0 = pool.actuallyPooledCurrencies[j].currency;
            const token1 = pool.actuallyPooledCurrencies[k].currency;
            pairs.push(convertPoolToTradingPair(pool, token0, token1));
        }
        if (!relatedMetaPools.length)
            continue;
        for (const otherMetaPool of relatedMetaPools) {
            for (const { currency } of otherMetaPool.actuallyPooledCurrencies) {
                if (isCurrencyEqual(currency, pool.lpToken))
                    continue;
                const token0 = pool.actuallyPooledCurrencies[j].currency;
                const token1 = currency;
                pairs.push(convertPoolAndBaseToTradingPair(pool, otherMetaPool, token0, token1));
            }
        }
    }
    return pairs;
};
// SOURCE: @zenlink-dex/sdk
// when swapping through metapool base->lpToken
const calculateStableSwapFromBase = (pool, basePool, tokenIndexFrom, tokenIndexTo, amount) => {
    const baseToken = basePool.lpToken;
    const baseTokenIndex = pool.getTokenIndex(baseToken);
    const baseAmounts = basePool.actuallyPooledCurrencies.map((amount) => new MonetaryAmount(amount.currency, 0));
    baseAmounts[tokenIndexFrom] = amount;
    const baseLpAmount = basePool.calculateTokenAmount(baseAmounts, true);
    if (baseTokenIndex === tokenIndexTo) {
        return baseLpAmount;
    }
    return pool.calculateSwap(baseTokenIndex, tokenIndexTo, baseLpAmount);
};
// when swapping through metapool lpToken->base
const calculateStableSwapToBase = (pool, basePool, tokenIndexFrom, tokenIndexTo, amount) => {
    const baseToken = basePool.lpToken;
    const baseTokenIndex = pool.getTokenIndex(baseToken);
    let tokenLPAmount = amount;
    if (baseTokenIndex !== tokenIndexFrom) {
        tokenLPAmount = pool.calculateSwap(tokenIndexFrom, baseTokenIndex, amount);
    }
    return basePool.calculateRemoveLiquidityOneToken(tokenLPAmount, tokenIndexTo)[0];
};
const getStableSwapOutputAmount = (path, inputAmount) => {
    if (!isCurrencyEqual(inputAmount.currency, path.input)) {
        throw new Error("getStableSwapOutputAmount: currencies of input amount and path element are different.");
    }
    let outputAmount;
    if (isStablePlainMultiPathElement(path)) {
        const fromIndex = path.pool.getTokenIndex(path.input);
        const toIndex = path.pool.getTokenIndex(path.output);
        outputAmount = path.pool.calculateSwap(fromIndex, toIndex, inputAmount);
    }
    else if (path.fromBase) {
        const fromIndex = path.basePool.getTokenIndex(path.input);
        const toIndex = path.pool.getTokenIndex(path.output);
        outputAmount = calculateStableSwapFromBase(path.pool, path.basePool, fromIndex, toIndex, inputAmount);
    }
    else {
        const fromIndex = path.pool.getTokenIndex(path.input);
        const toIndex = path.basePool.getTokenIndex(path.output);
        outputAmount = calculateStableSwapToBase(path.pool, path.basePool, fromIndex, toIndex, inputAmount);
    }
    return outputAmount;
};
export { getAllTradingPairs, getStableSwapOutputAmount, filterNonEmptyPools };
//# sourceMappingURL=utils.js.map